import { PublishedInvestmentManagerUpdatesDto, PublishedInvestmentManagerUpdatesSourceEnum } from '@/components/managerUpdates-page/types';
import { FirmInfoType } from '@/components/settings-page/platformSettings/firmInfo';
import { ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import { formatUserPhoneNumber } from '@/components/settings-page/users/firmTable/tableBody';
import { getCacheBustedFileUrl } from '@/components/settings-page/utils';
import { TableColumns } from '@/components/table/types';
import { Document, Page, Image as PdfImage, Text, View } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { DefaultTheme } from 'styled-components/dist/types';
import { UpdatesSummaryPDFTemplateFooter, UpdatesSummaryPDFTemplateHeader, UpdatesSummaryPDFTemplateTable } from './components';

interface UpdatesSummaryPDFTemplateProps {
  publishedUpdatesTableColumns: TableColumns[];
  publishedUpdates: PublishedInvestmentManagerUpdatesDto[];
  slideTitle: string;
  firmInfoData: FirmInfoType;
  templateStylingData: ITemplateStylingSettings;
  theme: DefaultTheme;
  source: PublishedInvestmentManagerUpdatesSourceEnum;
  t: TFunction<'enums', undefined, 'enums'>;
}

export const UpdatesSummaryPDFTemplate = ({
  publishedUpdatesTableColumns,
  publishedUpdates,
  slideTitle,
  firmInfoData,
  templateStylingData,
  theme,
  source,
  t
}: UpdatesSummaryPDFTemplateProps) => {
  const { firmName, address, website, phone } = firmInfoData || {};
  const { streetAddress, city, state, zipCode } = address || {};
  const hasPhone = phone && Object.values(phone || {})?.length > 0;
  const phoneNumber = hasPhone ? formatUserPhoneNumber(phone.countryCode, phone.number) : '';
  const currentDate = format(new Date(), 'MMMM yyyy');

  return (
    <Document>
      <Page
        size="A4"
        orientation="landscape"
        style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '40px', justifyContent: 'space-between' }}
      >
        <View style={{ height: '100px' }}>
          <PdfImage
            src={getCacheBustedFileUrl(templateStylingData?.logo)}
            style={{
              maxWidth: '150px',
              maxHeight: '100px',
              objectFit: 'contain',
              marginLeft: 'auto'
            }}
          />
        </View>
        <View style={{ display: 'flex', flex: 1, justifyContent: 'center', width: '100%' }}>
          <View
            style={{
              fontSize: '32px',
              padding: '0 8px 3px 8px',
              fontFamily: templateStylingData?.headerFontStyle?.fontFamily || 'Roboto Slab',
              color: templateStylingData?.headerFontStyle?.fontColor ? `#${templateStylingData?.headerFontStyle?.fontColor}` : theme.font.strong,
              borderBottom: `2px solid ${theme.layer[2]}`
            }}
          >
            <Text>{slideTitle}</Text>
          </View>
          <View
            style={{
              fontSize: '19px',
              padding: '10px 8px 0 8px',
              fontFamily: 'Open Sans',
              color: theme.font.base
            }}
          >
            <Text>Private Capital Summary</Text>
            <Text>{currentDate}</Text>
          </View>
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontSize: '10px',
            fontFamily: 'Open Sans',
            color: theme.font.weak
          }}
        >
          <Text style={{ fontWeight: 700 }}>Prepared By</Text>
          <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '5px' }}>
            <Text style={{ color: theme.font.base, fontWeight: 700 }}>{firmName}</Text>
            {streetAddress && <Text>{streetAddress}</Text>}
            {(city || state || zipCode) && <Text>{`${city ? ` ${city},` : ''} ${state || ''} ${zipCode || ''}`}</Text>}
            {hasPhone && <Text>{`${phoneNumber}`}</Text>}
            {website && <Text>{website}</Text>}
          </View>
        </View>
      </Page>

      <Page size="A4" orientation="landscape" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '80px 0 120px 0' }}>
        <UpdatesSummaryPDFTemplateHeader theme={theme} leftSideText="Private Capital Summary" rightSideText={currentDate} />
        <UpdatesSummaryPDFTemplateTable
          publishedUpdatesTableColumns={publishedUpdatesTableColumns}
          publishedUpdates={publishedUpdates}
          templateStylingData={templateStylingData}
          source={source}
          theme={theme}
          t={t}
        />
        <View
          style={{
            position: 'absolute',
            bottom: '80px',
            left: 0,
            right: 0,
            padding: '0 40px',
            fontSize: '8px',
            fontFamily: 'Open Sans',
            color: theme.font.weak
          }}
          fixed
        >
          <Text>
            Disclosure: This is a summary of the private investments currently held in your portfolio. The report provides projections based on
            various assumptions and are therefore hypothetical in nature and not guarantees of investment returns. You should consult your tax and/or
            legal advisors before implementing any transactions and/or strategies concerning your finances.
          </Text>
        </View>
        <UpdatesSummaryPDFTemplateFooter theme={theme} firmInfoData={firmInfoData} templateStylingData={templateStylingData} />
      </Page>

      {/* <Page size="A4" orientation="landscape" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '40px' }}>
        <View style={{ height: '100px' }}>
          <PdfImage
            src={getCacheBustedFileUrl(templateStylingData?.logo)}
            style={{
              maxWidth: '150px',
              maxHeight: '100px',
              objectFit: 'contain',
              marginLeft: 'auto'
            }}
          />
        </View>
        <View style={{ marginTop: '110px' }}>
          <View
            style={{
              fontSize: '32px',
              padding: '0 8px 3px 8px',
              fontFamily: templateStylingData?.headerFontStyle?.fontFamily || 'Roboto Slab',
              color: templateStylingData?.headerFontStyle?.fontColor ? `#${templateStylingData?.headerFontStyle?.fontColor}` : theme.font.strong,
              borderBottom: `2px solid ${theme.layer[2]}`
            }}
          >
            <Text>Disclosure</Text>
          </View>
        </View>
      </Page> */}

      {/* <Page size="A4" orientation="landscape" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '105px 0 120px 0' }}>
        <UpdatesSummaryPDFTemplateHeader theme={theme} leftSideText="Private Capital Summary and Timeline" rightSideText={`Updated ${currentDate}`} />
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            position: 'absolute',
            top: '63px',
            left: 0,
            height: '25px',
            width: '100%',
            padding: '0 60px',
            backgroundColor: theme.layer[12]
          }}
          fixed
        >
          <Text
            style={{
              fontSize: '12px',
              fontFamily: 'Open Sans',
              color: theme.font.base
            }}
          >
            Disclosure
          </Text>
        </View>

        <View style={{ padding: '0 40px' }}>
          <Text
            style={{
              fontSize: '8px',
              fontFamily: 'Open Sans',
              color: theme.font.weak
            }}
          >
            {UPDATES_SUMMARY_MOCKED_DISCLOSURE_TEXT}
          </Text>
        </View>

        <UpdatesSummaryPDFTemplateFooter theme={theme} firmInfoData={firmInfoData} templateStylingData={templateStylingData} />
      </Page> */}
    </Document>
  );
};
